import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GitBranch, Send, Loader2, Eye, Star, AlertCircle } from "lucide-react";

export default function AdminMatching() {
  const [requests, setRequests] = useState([]);
  const [selRequest, setSelRequest] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existingSl, setExistingSl] = useState(null);

  useEffect(() => { api.get("/admin/requests").then(r => setRequests(r.data || [])); }, []);

  const loadMatches = async (req) => {
    setSelRequest(req);
    setLoading(true);
    try {
      const r = await api.get(`/matching/request/${req.id}`);
      if (!r.data || r.data.length === 0) {
        const c = await api.post(`/matching/compute/${req.id}`);
        setMatches(c.data || []);
      } else {
        setMatches(r.data);
      }
      const all = await api.get("/shortlists/");
      const existing = (all.data || []).find(s => s.request_id === req.id);
      if (existing) {
        setExistingSl(existing);
        setShortlist(existing.manager_ids || []);
      } else {
        setExistingSl(null);
        setShortlist([]);
      }
    } catch (e) { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  const recompute = async () => {
    if (!selRequest) return;
    setLoading(true);
    try {
      const c = await api.post(`/matching/compute/${selRequest.id}`);
      setMatches(c.data || []);
      toast.success("Match score ricalcolato");
    } catch (e) { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  const toggleManager = (id) => {
    setShortlist(arr => arr.includes(id) ? arr.filter(x => x !== id) : (arr.length < 3 ? [...arr, id] : (toast.error("Max 3 manager"), arr)));
  };

  const saveShortlist = async () => {
    if (!selRequest || !shortlist.length) return;
    setLoading(true);
    try {
      const r = await api.post("/shortlists/", { request_id: selRequest.id, manager_ids: shortlist, notes: "Shortlist validata dal team CXOORA" });
      setExistingSl(r.data);
      toast.success("Shortlist salvata come bozza");
    } catch (e) { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  const publish = async () => {
    if (!existingSl) { toast.error("Salva prima la bozza"); return; }
    setLoading(true);
    try {
      await api.post(`/shortlists/${existingSl.id}/publish`);
      toast.success("Shortlist pubblicata. Azienda notificata.");
      setExistingSl({ ...existingSl, status: "published" });
    } catch (e) { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Matching & Shortlist</h1>
      <p className="text-sm text-slate-600 mb-6">Calcola il match score e costruisci una shortlist di max 3 manager.</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cxo-card p-4">
          <h2 className="font-semibold text-slate-900 mb-3">Richieste</h2>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto" data-testid="matching-requests-list">
            {requests.map(r => (
              <button key={r.id} onClick={() => loadMatches(r)} className={`w-full text-left p-3 rounded-lg text-sm ${selRequest?.id === r.id ? "bg-[#EAF0FF] border border-[#2D6BFF]" : "hover:bg-slate-50 border border-transparent"}`} data-testid={`select-req-${r.id}`}>
                <p className="font-medium text-slate-900 line-clamp-1">{r.title}</p>
                <p className="text-xs text-slate-500">{r.recommended_role}</p>
                <Badge variant="outline" className="text-[10px] mt-1">{r.crm_stage}</Badge>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2">
          {!selRequest ? (
            <div className="cxo-card p-10 text-center text-slate-500">Seleziona una richiesta a sinistra per visualizzare i match.</div>
          ) : (
            <div className="space-y-3">
              <div className="cxo-card p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="font-semibold text-slate-900">{selRequest.title}</h2>
                    <p className="text-xs text-slate-500">Ruolo cercato: {selRequest.recommended_role}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={recompute} disabled={loading}><GitBranch size={14} className="mr-1" /> Ricalcola</Button>
                    <Button size="sm" onClick={saveShortlist} disabled={!shortlist.length || loading} className="bg-[#2D6BFF] text-white" data-testid="save-shortlist-btn">{loading && <Loader2 size={14} className="mr-1 animate-spin" />} Salva bozza ({shortlist.length}/3)</Button>
                    <Button size="sm" onClick={publish} disabled={!existingSl || existingSl.status === "published"} className="bg-[#1F9D6A] text-white" data-testid="publish-shortlist-btn"><Send size={14} className="mr-1" /> Pubblica</Button>
                  </div>
                </div>
                {existingSl?.status === "published" && <Badge className="mt-3 bg-[#E9F8F1] text-[#1F9D6A] border-0">Shortlist pubblicata</Badge>}
              </div>

              <div className="space-y-2" data-testid="matches-list">
                {matches.slice(0, 10).map((m, idx) => {
                  const mp = m.manager;
                  const selected = shortlist.includes(m.manager_id);
                  return (
                    <div key={m.id} className={`cxo-card p-4 flex items-center gap-3 ${selected ? "border-2 border-[#2D6BFF]" : ""}`}>
                      <span className="text-xs font-semibold text-slate-400 w-6 cxo-tabular">#{idx + 1}</span>
                      <img src={mp?.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{mp?.user?.full_name}</p>
                        <p className="text-xs text-slate-600 truncate">{mp?.primary_role}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-xl font-bold cxo-tabular text-[#2D6BFF]">{m.total_score}%</p>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild><Button size="sm" variant="ghost"><Eye size={14} /></Button></DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader><DialogTitle>{mp?.user?.full_name} · {m.total_score}% match</DialogTitle></DialogHeader>
                          <div className="space-y-3 text-sm">
                            <div>
                              <p className="font-semibold text-slate-700 mb-1">Breakdown</p>
                              <div className="space-y-1">
                                {Object.entries(m.breakdown || {}).map(([k, v]) => (
                                  <div key={k} className="flex justify-between"><span className="text-slate-600 capitalize">{k}</span><span className="font-semibold cxo-tabular">{v}</span></div>
                                ))}
                              </div>
                            </div>
                            {m.reasons?.length > 0 && <div><p className="font-semibold text-slate-700 mb-1">Motivi positivi</p><ul className="space-y-1">{m.reasons.map((r, i) => <li key={i} className="flex gap-2"><Star size={14} className="text-[#1F9D6A] mt-0.5" />{r}</li>)}</ul></div>}
                            {m.concerns?.length > 0 && <div><p className="font-semibold text-slate-700 mb-1">Aree di attenzione</p><ul className="space-y-1">{m.concerns.map((r, i) => <li key={i} className="flex gap-2"><AlertCircle size={14} className="text-amber-600 mt-0.5" />{r}</li>)}</ul></div>}
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Checkbox checked={selected} onCheckedChange={() => toggleManager(m.manager_id)} data-testid={`select-mgr-${m.manager_id}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
